import {
  Box,
  Typography,
  Card,
  CardActionArea,
  Stack,
  Link,
} from "@mui/material";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "i18n-fe";
import { APPS, appUrl, canUseApp, PageLayout, StyledBreadcrumb, useActiveOrg } from "common-fe";

// #3656 -- the launcher grid reads the ONE shared app list (common-fe
// appList.jsx) and the ONE access rule (canUseApp, appAccess.js) that the
// avatar-menu app switcher and each app's own gate use, so a tile can never
// appear for an app whose gate would turn the user away.
//
// Behaviour note: Show Manager keeps its entitlement-only rule. Score Admin,
// Announcer and Scoreboard tiles now follow the same ROLE rules as their gates
// (they were entitlement-only before this card). Scribe stays entitlement-only.
// Super admins see every tile. A tile whose base URL is unset is hidden.
export default function Home() {
  const { t } = useTranslation("common");
  const auth = useAuth();
  const { activeOrgId, isSuper } = useActiveOrg();
  const accessToken = auth.user?.access_token;

  const apps = APPS.map((app) => ({ app, url: appUrl(app, import.meta.env) })).filter(
    ({ app, url }) => url && canUseApp(app.key, { accessToken, orgId: activeOrgId, isSuper })
  );

  const breadcrumbs = [
    <StyledBreadcrumb key="home" label={t("home.appsPageTitle")} />,
  ];

  return (
    <PageLayout maxWidth={1000} breadcrumbs={breadcrumbs}>
      {apps.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
          {t("home.noApps")}
        </Typography>
      ) : (
        <Box
          sx={{
            width: "100%",
            display: "grid",
            gap: 3,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(auto-fit, minmax(260px, 1fr))",
            },
          }}
        >
          {apps.map(({ app, url }) => (
            <Card
              key={app.key}
              sx={{
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
              }}
            >
              <CardActionArea
                component={Link}
                href={url}
                sx={{
                  height: "100%",
                  minHeight: 220,
                  p: 4,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "none" },
                }}
              >
                <Stack spacing={2} alignItems="center" textAlign="center">
                  <Box sx={{ color: "text.primary" }}>
                    <app.Icon sx={{ fontSize: 56 }} />
                  </Box>
                  <Typography variant="h5" sx={{ color: "text.primary" }}>
                    {app.title(t)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {app.description(t)}
                  </Typography>
                </Stack>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}
    </PageLayout>
  );
}
